import { forgetCodeLabels, useCodeLabels } from "../codes";
import { useEffect, useState, type ReactNode } from "react";
import {
  Body1,
  Button,
  Caption1,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Radio,
  RadioGroup,
  Select,
  Textarea,
} from "@fluentui/react-components";
import {
  addDays,
  CLOSE_REASONS,
  DEFAULT_PROBABILITY,
  isClosed,
  MAX_WEEKS,
  mondayOf,
  planEnd,
  newId,
  requiresReason,
  STATUS_LABEL,
  STATUSES,
  today,
  type Project,
  type ProjectStatus,
  type RangeOp,
  type Role,
  type WorkingPlan,
} from "../../../core";
import { api, errorText, send, type CodesResponse, type ProjectResponse } from "../api";
import { DateInput } from "../DateInput";
import { day, pct } from "../format";
import type { Block } from "./AllocationGrid";
import { ErrorBar, Warnings } from "../ui";

function Frame(props: { title: string; children: ReactNode; action: string; disabled?: boolean; busy?: boolean; error?: string; onAction: () => void; onClose: () => void; extra?: ReactNode; wide?: boolean }) {
  return (
    <Dialog open onOpenChange={(_, d) => !d.open && props.onClose()}>
      <DialogSurface className={props.wide ? "dialog-wide" : undefined}>
        <DialogBody>
          <DialogTitle>{props.title}</DialogTitle>
          <DialogContent className="dialog-stack">{props.children}</DialogContent>
          <ErrorBar message={props.error} />
          <DialogActions>
            {props.extra}
            <Button onClick={props.onClose}>Cancel</Button>
            <Button appearance="primary" disabled={props.disabled || props.busy} onClick={props.onAction}>
              {props.busy ? "Working…" : props.action}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

/** Add an assignment, or change one's role and person (PE3). A placeholder costs at its role's standard rate. */
export function AssignmentDialog(props: {
  row: number | null;
  plan: WorkingPlan;
  roles: Role[];
  people: ProjectResponse["people"];
  onClose: () => void;
  onApply: (plan: WorkingPlan) => void;
}) {
  const current = props.row === null ? null : props.plan.assignments[props.row];
  const [roleId, setRoleId] = useState(current?.roleId ?? props.roles.find((r) => r.active)?.id ?? "");
  const [personId, setPersonId] = useState<string>(current?.personId ?? "");
  const [note, setNote] = useState(current?.note ?? "");
  const working = props.people.filter((p) => p.status === "active" || p.id === current?.personId);
  const chosen = props.people.find((p) => p.id === personId);
  const apply = () => {
    const values = { roleId, personId: personId || null, note: note.trim() || undefined };
    const assignments = current
      ? props.plan.assignments.map((a, i) => (i === props.row ? { ...a, ...values } : a))
      : [...props.plan.assignments, { id: newId(), ...values, hours: new Array(props.plan.weeks).fill(0) }];
    props.onApply({ ...props.plan, assignments });
  };
  const remove = () => props.onApply({ ...props.plan, assignments: props.plan.assignments.filter((_, i) => i !== props.row) });
  const duplicate = () =>
    props.onApply({ ...props.plan, assignments: [...props.plan.assignments, { ...current!, id: newId(), personId: null, hours: [...current!.hours] }] });
  return (
    <Frame
      title={current ? "Change assignment" : "Add an assignment"}
      action={current ? "Apply" : "Add"}
      disabled={!roleId}
      onAction={apply}
      onClose={props.onClose}
      extra={
        current && (
          <>
            <Button appearance="subtle" onClick={duplicate}>
              Duplicate as placeholder
            </Button>
            <Button appearance="subtle" onClick={remove}>
              Remove row
            </Button>
          </>
        )
      }
    >
      <Field label="Role" required>
        <Select value={roleId} onChange={(_, d) => setRoleId(d.value)}>
          {props.roles.filter((r) => r.active || r.id === roleId).map((r) => (
            <option key={r.id} value={r.id}>
              {r.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Person" hint="A placeholder costs at the role's standard rate. Swapping in a person re-costs every week at their rate.">
        <Select value={personId} onChange={(_, d) => setPersonId(d.value)}>
          <option value="">Placeholder</option>
          {working.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.endDate ? ` (last day ${day(p.endDate)})` : ""}
            </option>
          ))}
        </Select>
      </Field>
      {chosen?.endDate && <Warnings items={[`${chosen.name}'s last day is ${day(chosen.endDate)}; hours after it still cost, but they have no capacity.`]} />}
      <Field label="Note">
        <Input value={note} onChange={(_, d) => setNote(d.value)} placeholder="e.g. covers the events workstream" />
      </Field>
      {current && <Caption1>The row keeps its hours. Use Remove row to drop it, or Undo to bring it back.</Caption1>}
    </Frame>
  );
}

/** Set, scale, shift or ramp the selected block (PE5). */
export function RangeDialog(props: { block: Block; plan: WorkingPlan; onClose: () => void; onApply: (op: RangeOp) => void }) {
  const [kind, setKind] = useState<RangeOp["kind"]>("set");
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const x = Number(a);
  const y = Number(b);
  const op: RangeOp | null =
    kind === "set" && a !== "" && x >= 0
      ? { kind, value: x }
      : kind === "scale" && a !== "" && x >= 0
        ? { kind, percent: x }
        : kind === "shift" && a !== "" && Number.isInteger(x) && x !== 0
          ? { kind, weeks: x }
          : kind === "ramp" && a !== "" && b !== "" && x >= 0 && y >= 0
            ? { kind, from: x, to: y }
            : null;
  const weeks = props.block.last - props.block.first + 1;
  const from = addDays(props.plan.startMonday, props.block.first * 7);
  return (
    <Frame title="Change the selected hours" action="Apply" disabled={!op} onAction={() => op && props.onApply(op)} onClose={props.onClose}>
      <Body1>
        {props.block.rows.length === 1 ? "1 row" : `${props.block.rows.length} rows`}, {weeks === 1 ? "1 week" : `${weeks} weeks`} from {day(from)}.
      </Body1>
      <RadioGroup value={kind} onChange={(_, d) => setKind(d.value as RangeOp["kind"])}>
        <Radio value="set" label="Set every week to" />
        <Radio value="scale" label="Scale by a percentage" />
        <Radio value="shift" label="Shift by weeks (negative moves earlier)" />
        <Radio value="ramp" label="Ramp from one level to another" />
      </RadioGroup>
      <div className="form-grid">
        <Field label={kind === "set" ? "Hours a week" : kind === "scale" ? "Percent (100 keeps it)" : kind === "shift" ? "Weeks" : "From hours a week"}>
          <Input type="number" value={a} onChange={(_, d) => setA(d.value)} autoFocus />
        </Field>
        {kind === "ramp" && (
          <Field label="To hours a week">
            <Input type="number" value={b} onChange={(_, d) => setB(d.value)} />
          </Field>
        )}
      </div>
      {kind === "shift" && <Caption1>Hours pushed past either end of the plan are dropped; lengthen the plan first to keep them.</Caption1>}
    </Frame>
  );
}

/** Status changes (§3.10). Lost and cancelled need a reason and a date; booking takes a baseline (PE14). */
export function StatusDialog(props: { project: Project; etag: string; onClose: (changed: boolean) => void }) {
  const { label: codeLabel } = useCodeLabels();
  const [status, setStatus] = useState<ProjectStatus>(props.project.status);
  const [date, setDate] = useState(today());
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [probability, setProbability] = useState(props.project.probability === undefined ? "" : String(Math.round(props.project.probability * 100)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const needsReason = requiresReason(status);
  const reasons = status === "lost" || status === "cancelled" ? CLOSE_REASONS[status] : [];
  const valid = !needsReason || (reason && date);

  async function save() {
    setBusy(true);
    setError(undefined);
    try {
      await send("POST", `/projects/${encodeURIComponent(props.project.id)}/status`, {
        etag: props.etag,
        status,
        date: isClosed(status) ? date : undefined,
        reason: needsReason ? reason : undefined,
        note: note || undefined,
        probability: probability === "" || isClosed(status) || status === "booked" ? undefined : Number(probability) / 100,
      });
      props.onClose(true);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Frame title={`Status: ${props.project.code ? codeLabel(props.project.code) : ""} — ${props.project.name}`} action="Change status" disabled={!valid} busy={busy} error={error} onAction={() => void save()} onClose={() => props.onClose(false)}>
      <Field label="Status">
        <Select value={status} onChange={(_, d) => setStatus(d.value as ProjectStatus)}>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </Select>
      </Field>
      {status === "booked" && props.project.status !== "booked" && (
        <Warnings intent="info" items={[`Booking takes Baseline ${props.project.baselines + 1}: a frozen copy of this plan and today's rates, which actuals are measured against.`]} />
      )}
      {status === "cancelled" && <Warnings intent="info" items={["Weeks from the cancellation date are released from everyone's load at once. Work and payments before it stay."]} />}
      {isClosed(status) && (
        <Field label={status === "cancelled" ? "Work stops on" : status === "lost" ? "Decided on" : "Delivered on"} required={needsReason}>
          <DateInput type="date" value={date} onChange={(_, d) => setDate(d.value)} />
        </Field>
      )}
      {needsReason && (
        <>
          <Field label="Reason" required>
            <Select value={reason} onChange={(_, d) => setReason(d.value)}>
              <option value="">Pick a reason</option>
              {reasons.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </Select>
          </Field>
          <Field label="Note">
            <Textarea value={note} onChange={(_, d) => setNote(d.value)} />
          </Field>
        </>
      )}
      {!isClosed(status) && status !== "booked" && (
        <Field label="Probability" hint={`Leave empty for the ${STATUS_LABEL[status].toLowerCase()} default, ${pct(DEFAULT_PROBABILITY[status], 0)}.`}>
          <Input type="number" min={0} max={100} contentAfter="%" value={probability} onChange={(_, d) => setProbability(d.value)} />
        </Field>
      )}
    </Frame>
  );
}

/** Hand the project to someone on the People list. Each change is kept in the project's owner history. */
export function OwnerDialog(props: { project: Project; etag: string; people: ProjectResponse["people"]; onClose: (changed: boolean) => void }) {
  const { label: codeLabel } = useCodeLabels();
  const [personId, setPersonId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const choices = props.people.filter((p) => p.status !== "archived" && p.id !== props.project.ownerPersonId).sort((a, b) => a.name.localeCompare(b.name));
  const chosen = props.people.find((p) => p.id === personId);

  async function save() {
    setBusy(true);
    setError(undefined);
    try {
      await send("POST", `/projects/${encodeURIComponent(props.project.id)}/owner`, { etag: props.etag, personId, note: note.trim() || undefined });
      props.onClose(true);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Frame title={`Owner: ${props.project.code ? codeLabel(props.project.code) : ""} — ${props.project.name}`} action="Change owner" disabled={!personId} busy={busy} error={error} onAction={() => void save()} onClose={() => props.onClose(false)}>
      <Body1>Owned now by {props.project.ownerName ?? "no one"}.</Body1>
      <Field label="New owner" required>
        <Select value={personId} onChange={(_, d) => setPersonId(d.value)}>
          <option value="">Pick a person</option>
          {choices.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
      </Field>
      {chosen && chosen.hasEmail === false && (
        <Warnings items={[`${chosen.name} has no email on the People list, so a Planner-level sign-in can't match them to this project. Add their email on their card.`]} />
      )}
      <Field label="Note" hint="Optional, e.g. why it changed hands.">
        <Input value={note} onChange={(_, d) => setNote(d.value)} />
      </Field>
      <Caption1>The change is recorded with today's date and your name.</Caption1>
    </Frame>
  );
}

/**
 * Copy a project, or start one from a template (PE10, PE11): a new code and name, a new start, and
 * which elements to keep. From a template you can also change the length, keeping either hours a
 * week or total hours.
 *
 * As a potential renewal (PF3, rev. 1.20), the copy is an opportunity that records which project it
 * renews, and starts the Monday after that project ends.
 */
export function CopyDialog(props: {
  source: Pick<Project, "id" | "name" | "isTemplate" | "plan" | "clientName" | "billable">;
  codes: CodesResponse["codes"];
  renewal?: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const fromTemplate = props.source.isTemplate;
  const renewal = !!props.renewal && !fromTemplate;
  const [code, setCode] = useState("");
  const [name, setName] = useState(fromTemplate ? "" : renewal ? `${props.source.name} renewal` : `${props.source.name} (copy)`);
  const [client, setClient] = useState(fromTemplate ? "" : props.source.clientName);
  const [start, setStart] = useState(renewal ? addDays(planEnd(props.source.plan), 1) : mondayOf(addDays(today(), 7)));
  const [keep, setKeep] = useState({ keepPeople: !fromTemplate, keepHours: true, keepSchedule: true, keepExpenses: true });
  const [weeks, setWeeks] = useState(String(props.source.plan.weeks));
  const [stretch, setStretch] = useState<"rate" | "total">("total");
  const [asTemplate, setAsTemplate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const w = Math.round(Number(weeks));
  const changedLength = w !== props.source.plan.weeks;
  const valid = name.trim() && (asTemplate || code.trim()) && w >= 1 && w <= MAX_WEEKS;

  async function create() {
    setBusy(true);
    setError(undefined);
    try {
      forgetCodeLabels(); // the next screen reads the codes afresh, this one with them
      const { project } = await send<{ project: { id: string } }>("POST", "/projects", {
        project: {
          code: asTemplate ? "" : code,
          name,
          clientName: client,
          billable: props.source.billable,
          isTemplate: asTemplate,
          ...(renewal && !asTemplate ? { status: "opportunity", renewalOf: props.source.id } : {}),
        },
        startMonday: mondayOf(start),
        from: { projectId: props.source.id, ...keep, weeks: w, stretch },
      });
      props.onCreated(project.id);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Frame title={fromTemplate ? `New project from ${props.source.name}` : renewal ? `Potential renewal of ${props.source.name}` : `Copy ${props.source.name}`} action="Create" disabled={!valid} busy={busy} error={error} onAction={() => void create()} onClose={props.onClose} wide>
      <div className="form-grid">
        {!asTemplate && (
          <Field label="Project code" required hint="Reported hours map to the project by this code.">
            <Input
              list="copy-codes"
              value={code}
              onChange={(_, d) => {
                const c = d.value.toUpperCase();
                setCode(c);
                const listed = props.codes.find((x) => x.code === c);
                if (listed && !client) setClient(listed.client);
              }}
            />
          </Field>
        )}
        <Field label="Name" required>
          <Input value={name} onChange={(_, d) => setName(d.value)} />
        </Field>
        <Field label="Client">
          <Input value={client} onChange={(_, d) => setClient(d.value)} />
        </Field>
        <Field label="Starts" hint="Moves to the Monday of that week. Every week, payment and cost moves with it.">
          <DateInput type="date" value={start} onChange={(_, d) => setStart(d.value)} />
        </Field>
        <Field label="Weeks" hint={`The source runs ${props.source.plan.weeks} weeks.`}>
          <Input type="number" min={1} max={MAX_WEEKS} value={weeks} onChange={(_, d) => setWeeks(d.value)} />
        </Field>
        {changedLength && (
          <Field label="When the length changes">
            <RadioGroup value={stretch} onChange={(_, d) => setStretch(d.value as "rate" | "total")}>
              <Radio value="total" label="Keep total hours (hours a week rescale)" />
              <Radio value="rate" label="Keep hours a week (total hours change)" />
            </RadioGroup>
          </Field>
        )}
      </div>
      {renewal && <Caption1>The renewal is created as an opportunity, linked to {props.source.name}, so Profitability can switch it on and off. Change its status and probability once you know more.</Caption1>}
      <Caption1>Keep from the source:</Caption1>
      <div className="checks">
        <Checkbox label="Named people (otherwise placeholders)" checked={keep.keepPeople} onChange={(_, d) => setKeep({ ...keep, keepPeople: !!d.checked })} />
        <Checkbox label="Hours" checked={keep.keepHours} onChange={(_, d) => setKeep({ ...keep, keepHours: !!d.checked })} />
        <Checkbox label="Payment schedule" checked={keep.keepSchedule} onChange={(_, d) => setKeep({ ...keep, keepSchedule: !!d.checked })} />
        <Checkbox label="Other costs" checked={keep.keepExpenses} onChange={(_, d) => setKeep({ ...keep, keepExpenses: !!d.checked })} />
        {!renewal && <Checkbox label="Save the copy as a template" checked={asTemplate} onChange={(_, d) => setAsTemplate(!!d.checked)} />}
      </div>
      {changedLength && keep.keepSchedule && <Caption1>The payment schedule moves to the new start but keeps its own length; adjust it after.</Caption1>}
      <datalist id="copy-codes">
        {props.codes.filter((c) => !c.retired).map((c) => (
          <option key={c.code} value={c.code}>
            {c.client}
          </option>
        ))}
      </datalist>
    </Frame>
  );
}

/**
 * Delete a project created in error (rev. 1.14). Says what goes with it, and asks for the code typed
 * out. Reported hours stay: they belong to the code, and a code still on the Projects list keeps them.
 */
export function DeleteDialog(props: { project: Project; etag: string; onClose: () => void; onDeleted: () => void }) {
  const { label: codeLabel } = useCodeLabels();
  const [usage, setUsage] = useState<{ baselines: number; hoursOnCode: number } | null>(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const expected = props.project.isTemplate ? props.project.name : props.project.code;
  useEffect(() => {
    void api<{ baselines: number; hoursOnCode: number }>(`/projects/${encodeURIComponent(props.project.id)}/usage`)
      .then(setUsage)
      .catch((err) => setError(errorText(err)));
  }, [props.project.id]);
  async function remove() {
    setBusy(true);
    setError(undefined);
    try {
      await send("POST", `/projects/${encodeURIComponent(props.project.id)}/delete`, { etag: props.etag, confirm: typed });
      props.onDeleted();
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }
  return (
    <Frame
      title={`Delete ${props.project.isTemplate ? props.project.name : `${codeLabel(props.project.code)} — ${props.project.name}`}?`}
      action="Delete"
      disabled={typed.trim().toUpperCase() !== expected.trim().toUpperCase() || !usage}
      busy={busy}
      error={error}
      onAction={() => void remove()}
      onClose={props.onClose}
    >
      <Body1>
        This removes the project, its plan, payment schedule and costs
        {usage?.baselines ? `, and its ${usage.baselines} ${usage.baselines === 1 ? "baseline" : "baselines"}` : ""}. It can't be undone here; the audit log keeps
        a copy of what was deleted.
      </Body1>
      {usage && usage.hoursOnCode > 0 && (
        <Warnings items={[`${usage.hoursOnCode} reported hours count toward ${props.project.code}. They stay, because they belong to the code; recode them in the inbox or on time cards if the code was wrong.`]} />
      )}
      <Field label={`Type ${expected} to confirm`}>
        <Input value={typed} onChange={(_, d) => setTyped(d.value)} autoFocus />
      </Field>
    </Frame>
  );
}
